import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
} from "lucide-react";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  useConversationBySourceId,
  useThreadMessages,
  useEnrichment,
  useTicketDetail,
} from "@/hooks/use-kb-insights";
import { ConversationView } from "@/components/conversations/ConversationView";
import { useAllTenants } from "@/hooks/use-tenants";
import { CellFailuresAlert } from "@/components/cells/cell-failures-alert";
import { CELLS, PRIMARY_CELL, findCell } from "@/lib/cells/cells";
import { fromTimeline } from "@/lib/conversation-envelope";
import type { Recommendation, ThreadMessage } from "@/hooks/use-kb-insights";

// ---- Timeline --------------------------------------------------------

type TimelineEntryKind = "chatbot" | "thread" | "internal-note";

interface TimelineEntry {
  id: string;
  kind: TimelineEntryKind;
  role: "user" | "agent";
  content: string;
  timestamp: string;
  endTimestamp?: string | null;
  senderLabel?: string | null;
  isInternal: boolean;
}

function buildTimeline(
  chatbotTurns: {
    id: string;
    role: string;
    message_text: string;
    timestamp: Date;
    end_timestamp?: Date | null;
    employee_id?: string | null;
    employee_name?: string | null;
  }[],
  threadMessages: ThreadMessage[],
): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  // Build employee_id → label map.
  // Reporter = first employee in thread. Assignee = first
  // different employee. Others get numbered labels.
  const nameMap = new Map<string, string>();
  let employeeCounter = 0;
  let reporterId: string | null = null;
  let assigneeId: string | null = null;

  const assignLabel = (
    employeeId: string | null,
    employeeName: string | null,
  ): string => {
    const key = employeeId ?? employeeName;
    if (!key) return "Employee";
    const existing = nameMap.get(key);
    if (existing) return existing;
    let label: string;
    if (!reporterId) {
      reporterId = key;
      label = employeeName ? `${employeeName} (Reporter)` : "Reporter";
    } else if (!assigneeId) {
      assigneeId = key;
      label = employeeName ? `${employeeName} (Assignee)` : "Assignee";
    } else {
      employeeCounter++;
      label = employeeName ?? `Employee #${employeeCounter}`;
    }
    nameMap.set(key, label);
    return label;
  };

  // Pre-populate roles from ALL thread messages (chronological).
  let chatbotUserName: string | null = null;
  for (const t of chatbotTurns) {
    if (t.role === "user" && t.employee_name) {
      chatbotUserName = t.employee_name;
      break;
    }
  }

  const sortedThreadMsgs = [...threadMessages].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );
  for (const msg of sortedThreadMsgs) {
    if (msg.role !== "employee" || !msg.employee_id) continue;
    if (nameMap.has(msg.employee_id)) continue;
    assignLabel(msg.employee_id, nameMap.size === 0 ? chatbotUserName : null);
  }

  for (const turn of chatbotTurns) {
    entries.push({
      id: `cb-${turn.id}`,
      kind: "chatbot",
      role: turn.role === "user" ? "user" : "agent",
      content: turn.message_text,
      timestamp: String(turn.timestamp),
      endTimestamp: turn.end_timestamp ? String(turn.end_timestamp) : null,
      senderLabel:
        turn.role === "user"
          ? turn.employee_name
            ? `${turn.employee_name} (Reporter)`
            : "Reporter"
          : "Harmony",
      isInternal: false,
    });
  }

  // Skip thread messages that overlap with chatbot turns.
  // Add 2s buffer for timestamp precision differences.
  const lastChatbotTs =
    chatbotTurns.length > 0
      ? Math.max(
          ...chatbotTurns.map((t) => {
            const ts = t.end_timestamp ?? t.timestamp;
            return new Date(String(ts)).getTime();
          }),
        ) + 2000
      : 0;

  for (const msg of threadMessages) {
    if (!msg.content) continue;
    if (lastChatbotTs > 0 && new Date(msg.timestamp).getTime() <= lastChatbotTs)
      continue;
    const isInternal = msg.is_internal;

    let senderLabel: string | null = null;
    if (msg.role === "employee") {
      senderLabel = assignLabel(msg.employee_id, null);
    } else if (msg.role === "agent") {
      senderLabel = "IT Agent";
    } else {
      senderLabel = msg.system_actor ?? "System";
    }

    entries.push({
      id: `th-${msg.id}`,
      kind: isInternal ? "internal-note" : "thread",
      role: msg.role === "employee" ? "user" : "agent",
      content: msg.content,
      timestamp: msg.timestamp,
      senderLabel,
      isInternal,
    });
  }

  entries.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  return entries;
}

// ---- Component -------------------------------------------------------

interface ConversationModalProps {
  recommendation: Recommendation;
  conversationIndex: number;
  open: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

/** Internal notes are opt-in in the viewer, but in a KB-insight review they
 *  are often the point — so this view opens with them on.
 */
const MODAL_TRACKS: readonly string[] = ["internal"];

export function ConversationModal({
  recommendation,
  conversationIndex,
  open,
  onClose,
  onNavigate,
}: ConversationModalProps) {
  const evidenceRaw = recommendation.evidence as unknown as Record<
    string,
    unknown
  >;
  const conversationIds: string[] =
    (evidenceRaw.conversation_ids as string[] | undefined) ||
    (evidenceRaw.supporting_conversation_ids as string[] | undefined) ||
    [];

  const total = conversationIds.length;
  // eslint-disable-next-line security/detect-object-injection
  const sourceId = conversationIds[conversationIndex] ?? null;

  const isTicketSource =
    (recommendation as Record<string, unknown>).source === "tickets";

  // The cell the recommendation's tenant lives in; with one cell, that cell.
  // A tenant no cell lists is read from the primary cell, as elsewhere.
  const {
    data: tenants,
    isLoading: tenantsLoading,
    failures: tenantFailures,
  } = useAllTenants();
  const listedCell = findCell(
    tenants?.find((t) => t.id === recommendation.tenant_id)?.deployment_cell,
  );
  const recommendationCell =
    CELLS.length > 1
      ? (listedCell ??
        (!tenantsLoading && tenantFailures.length === 0
          ? PRIMARY_CELL
          : undefined))
      : PRIMARY_CELL;
  // The tenant's cell can't be told because a cell's tenants didn't load.
  const cellUnknown =
    !recommendationCell && !tenantsLoading && tenantFailures.length > 0;

  // Conversation path (chatbot + thread)
  const { data: conversation, isLoading: isLoadingConv } =
    useConversationBySourceId(
      isTicketSource ? null : sourceId,
      recommendation.tenant_id,
    );
  const { data: threadMessages, isLoading: isLoadingThread } =
    useThreadMessages(
      isTicketSource ? null : sourceId,
      recommendation.tenant_id,
    );

  // Ticket path
  const { data: ticketDetail, isLoading: isLoadingTicket } = useTicketDetail(
    isTicketSource ? sourceId : null,
    recommendationCell,
  );

  // Enrichments (only for conversations)
  const { data: enrichment, isLoading: isLoadingEnrichment } = useEnrichment(
    isTicketSource ? null : sourceId,
    recommendation.tenant_id,
  );

  const isLoading = isTicketSource
    ? isLoadingTicket || (!recommendationCell && tenantsLoading)
    : isLoadingConv || isLoadingThread;

  const timeline = useMemo(() => {
    if (isTicketSource && ticketDetail) {
      return ticketDetail.messages.map(
        (m, i): TimelineEntry => ({
          id: `ticket-${i}`,
          kind: m.is_internal ? "internal-note" : "thread",
          role: m.is_internal ? "agent" : "user",
          content: m.content,
          timestamp: m.created_at,
          senderLabel:
            m.employee_name || (m.is_internal ? "Internal" : "Employee"),
          isInternal: m.is_internal,
        }),
      );
    }
    const turns = conversation?.turns ?? [];
    const msgs = threadMessages ?? [];
    return buildTimeline(turns, msgs);
  }, [isTicketSource, ticketDetail, conversation?.turns, threadMessages]);

  const enrichmentItems = isTicketSource
    ? buildEnrichmentItems(ticketDetail ?? null)
    : buildEnrichmentItems(enrichment);

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex w-[600px] flex-col p-0 sm:max-w-[600px]"
      >
        {isTicketSource && cellUnknown && (
          <div className="shrink-0 px-5 pt-5">
            <CellFailuresAlert
              failures={tenantFailures}
              heading={(cell) =>
                `Can't load this ticket: ${cell.residency} tenants didn't load`
              }
              signInInNewTab
            />
          </div>
        )}
        {/* Header */}
        <div className="shrink-0 border-b px-5 pb-3 pt-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-base font-semibold">
                {isTicketSource && sourceId
                  ? `Ticket ${sourceId}`
                  : `Source ${conversationIndex + 1} / ${total}`}
              </SheetTitle>
              {isTicketSource && ticketDetail && (
                <p className="text-muted-foreground mt-0.5 text-sm">
                  {ticketDetail.subject}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1 pt-0.5">
              <Button
                size="icon"
                variant="outline"
                className="size-8"
                disabled={conversationIndex <= 0}
                onClick={() => {
                  onNavigate(conversationIndex - 1);
                }}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <Button
                size="icon"
                variant="outline"
                className="size-8"
                disabled={conversationIndex >= total - 1}
                onClick={() => {
                  onNavigate(conversationIndex + 1);
                }}
              >
                <ChevronRight className="size-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="ml-1 size-8"
                onClick={onClose}
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-5 py-4">
            {/* Enrichments */}
            {isLoadingEnrichment && !isTicketSource && (
              <div className="bg-muted/20 text-muted-foreground mb-4 flex items-center gap-2 rounded-lg border p-4 text-xs">
                <Loader2 className="size-3.5 animate-spin" />
                Loading enrichments...
              </div>
            )}
            {((!isLoadingEnrichment && enrichmentItems.length > 0) ||
              isLoadingTicket) && (
              <div className="bg-muted/20 mb-4 rounded-lg border p-4">
                <p className="text-muted-foreground mb-3 text-[10px] font-semibold uppercase tracking-wider">
                  Enrichments
                </p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  {enrichmentItems.map((item) => (
                    <div
                      key={item.label}
                      className={item.fullWidth ? "col-span-2" : ""}
                    >
                      <p className="text-muted-foreground mb-1 text-[10px] font-semibold uppercase tracking-wider">
                        {item.label}
                      </p>
                      {item.list ? (
                        <ul className="space-y-0.5">
                          {item.list.map((li) => (
                            <li
                              key={li}
                              className="flex items-start gap-1.5 text-sm leading-relaxed"
                            >
                              <span className="bg-muted-foreground/40 mt-1.5 size-1 shrink-0 rounded-full" />
                              {li}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm leading-relaxed">{item.value}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ticket details */}
            {isTicketSource && ticketDetail && (
              <div className="bg-muted/10 mb-4 space-y-2 rounded-lg border p-4">
                <p className="text-muted-foreground mb-3 text-[10px] font-semibold uppercase tracking-wider">
                  Ticket Details
                </p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">ID:</span>{" "}
                    <span className="font-mono">
                      {ticketDetail.external_id}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>{" "}
                    {ticketDetail.status}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Type:</span>{" "}
                    {ticketDetail.ticket_type}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Category:</span>{" "}
                    {ticketDetail.category}
                    {ticketDetail.sub_category
                      ? ` / ${ticketDetail.sub_category}`
                      : ""}
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Subject:</span>{" "}
                    {ticketDetail.subject}
                  </div>
                </div>
              </div>
            )}

            {/* Conversation timeline */}
            <div className="space-y-3">
              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="text-muted-foreground size-6 animate-spin" />
                </div>
              ) : timeline.length > 0 ? (
                <ConversationView
                  data={fromTimeline(timeline)}
                  show={MODAL_TRACKS}
                />
              ) : (
                <div className="text-muted-foreground py-16 text-center text-sm">
                  {sourceId
                    ? "No conversation found for this source."
                    : "Conversation ID not available."}
                </div>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---- Helpers ---------------------------------------------------------

interface EnrichmentItem {
  label: string;
  value: string;
  list?: string[];
  fullWidth: boolean;
}

function buildEnrichmentItems(
  enrichment:
    | {
        category?: string;
        sub_category?: string;
        user_intent?: string;
        entities?: string;
        resolution_insights?: string;
      }
    | null
    | undefined,
): EnrichmentItem[] {
  if (!enrichment) return [];
  const items: EnrichmentItem[] = [];

  if (enrichment.category) {
    items.push({
      label: "Category",
      value: enrichment.sub_category
        ? `${enrichment.category} / ${enrichment.sub_category}`
        : enrichment.category,
      fullWidth: false,
    });
  }
  if (enrichment.user_intent) {
    items.push({
      label: "User Intent",
      value: enrichment.user_intent,
      fullWidth: false,
    });
  }
  if (enrichment.entities && enrichment.entities !== "[]") {
    const parsed = parseJsonArray(enrichment.entities);
    items.push({
      label: "Entities",
      value: enrichment.entities,
      list: parsed ?? undefined,
      fullWidth: false,
    });
  }
  if (
    enrichment.resolution_insights &&
    enrichment.resolution_insights !== "[]"
  ) {
    const parsed = parseJsonArray(enrichment.resolution_insights);
    items.push({
      label: "Resolution Insights",
      value: enrichment.resolution_insights,
      list: parsed ?? undefined,
      fullWidth: true,
    });
  }

  return items;
}

function parseJsonArray(value: string): string[] | null {
  if (!value.startsWith("[")) return null;
  try {
    const arr = JSON.parse(value) as unknown;
    if (Array.isArray(arr) && arr.every((i) => typeof i === "string")) {
      return arr;
    }
    return null;
  } catch {
    return null;
  }
}

